import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMap } from '../map/MapContext';

interface TrackPoint {
  timestamp: number;
  latitude: number;
  longitude: number;
  altitudeFt: number;
  groundSpeedKts: number;
  heading: number;
}

type RecordingState = 'idle' | 'recording' | 'paused';

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export const TrackLogPanel: React.FC = () => {
  const { map } = useMap();
  const [state, setState] = useState<RecordingState>('idle');
  const [points, setPoints] = useState<TrackPoint[]>([]);
  const [elapsed, setElapsed] = useState(0);
  const [distanceNm, setDistanceNm] = useState(0);
  const startTimeRef = useRef(0);
  const intervalRef = useRef<number | null>(null);

  // Timer tick
  useEffect(() => {
    if (state !== 'recording') return;
    intervalRef.current = window.setInterval(() => {
      setElapsed(Date.now() - startTimeRef.current);
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [state]);

  // GPS tracking (Geolocation API)
  useEffect(() => {
    if (state !== 'recording' || !navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const pt: TrackPoint = {
          timestamp: Date.now(),
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          altitudeFt: (pos.coords.altitude ?? 0) * 3.28084,
          groundSpeedKts: (pos.coords.speed ?? 0) * 1.94384,
          heading: pos.coords.heading ?? 0,
        };
        setPoints((prev) => {
          if (prev.length > 0) {
            const last = prev[prev.length - 1];
            const dLat = (pt.latitude - last.latitude) * 60;
            const dLng = (pt.longitude - last.longitude) * 60 * Math.cos((pt.latitude * Math.PI) / 180);
            const legNm = Math.sqrt(dLat * dLat + dLng * dLng);
            setDistanceNm((d) => d + legNm);
          }
          return [...prev, pt];
        });
      },
      undefined,
      { enableHighAccuracy: true, maximumAge: 1000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [state]);

  const startRecording = useCallback(() => {
    startTimeRef.current = Date.now();
    setElapsed(0);
    setDistanceNm(0);
    setPoints([]);
    setState('recording');
  }, []);

  const pauseRecording = useCallback(() => setState('paused'), []);
  const resumeRecording = useCallback(() => {
    startTimeRef.current = Date.now() - elapsed;
    setState('recording');
  }, [elapsed]);
  const stopRecording = useCallback(() => setState('idle'), []);

  const exportGPX = useCallback(() => {
    const trkpts = points.map((p) =>
      `    <trkpt lat="${p.latitude}" lon="${p.longitude}"><ele>${(p.altitudeFt / 3.28084).toFixed(1)}</ele><time>${new Date(p.timestamp).toISOString()}</time></trkpt>`
    ).join('\n');
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="EFB Web"><trk><name>Track ${new Date().toISOString()}</name><trkseg>
${trkpts}
</trkseg></trk></gpx>`;
    const blob = new Blob([gpx], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `track-${Date.now()}.gpx`; a.click();
    URL.revokeObjectURL(url);
  }, [points]);

  const maxAlt = points.length > 0 ? Math.max(...points.map((p) => p.altitudeFt)) : 0;
  const maxSpd = points.length > 0 ? Math.max(...points.map((p) => p.groundSpeedKts)) : 0;

  return (
    <div>
      {/* Timer display */}
      <div className="panel-section" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 32, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
          {formatElapsed(elapsed)}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {state === 'recording' && <span className="status-green">⏺ Recording</span>}
          {state === 'paused' && <span className="status-orange">⏸ Paused</span>}
          {state === 'idle' && <span>Ready</span>}
        </div>
      </div>

      {/* Controls */}
      <div className="panel-section">
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          {state === 'idle' && (
            <button className="btn btn-primary" onClick={startRecording}>⏺ Start Recording</button>
          )}
          {state === 'recording' && (
            <>
              <button className="btn btn-secondary" onClick={pauseRecording}>⏸ Pause</button>
              <button className="btn" style={{ background: 'var(--red)', color: '#fff' }} onClick={stopRecording}>⏹ Stop</button>
            </>
          )}
          {state === 'paused' && (
            <>
              <button className="btn btn-primary" onClick={resumeRecording}>▶ Resume</button>
              <button className="btn" style={{ background: 'var(--red)', color: '#fff' }} onClick={stopRecording}>⏹ Stop</button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="panel-section">
        <div className="panel-section-title">Flight Stats</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { label: 'Distance', value: `${distanceNm.toFixed(1)} NM` },
            { label: 'Points', value: String(points.length) },
            { label: 'Max Alt', value: `${maxAlt.toFixed(0)} ft` },
            { label: 'Max GS', value: `${maxSpd.toFixed(0)} kts` },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: 'var(--bg-input)', borderRadius: 6, padding: 8 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Export */}
      {points.length > 0 && state === 'idle' && (
        <div className="panel-section">
          <button className="btn btn-secondary" onClick={exportGPX} style={{ width: '100%' }}>📄 Export GPX</button>
        </div>
      )}
    </div>
  );
};
